/**
 * Camera Capture Component
 * Enables taking photos with device camera for medication images
 */

import { Camera, Check, RotateCw, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { log } from "@/lib/log";
import { useI18nStore } from "@/stores/i18nStore";

interface CameraCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  title: string;
}

export const CameraCapture: React.FC<CameraCaptureProps> = ({
  isOpen,
  onClose,
  onCapture,
  title,
}) => {
  const { t } = useI18nStore();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">(
    "environment",
  );
  // Only a flag: the message is translated at render so it follows the
  // active language.
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isOpen, facingMode]);

  const startCamera = async () => {
    try {
      setError(false);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err: any) {
      console.error("Camera access error:", err);
      setError(true);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setCapturedImage(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (!context) return;

    // Set canvas dimensions to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Get image as data URL
    const imageDataUrl = canvas.toDataURL("image/jpeg", 0.9);

    // Stop camera stream after capture
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }

    setCapturedImage(imageDataUrl);
    log.debug("📸 Photo captured, camera stream stopped");
  };

  const switchCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    // Restart camera after retake
    startCamera();
    log.debug("🔄 Retaking photo, restarting camera");
  };

  const confirmPhoto = () => {
    if (!capturedImage) return;

    log.debug("✅ Confirming photo, converting to file...");

    // Convert data URL to File
    fetch(capturedImage)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], `camera-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        log.debug("✅ File created from camera:", {
          name: file.name,
          size: file.size,
          type: file.type,
        });
        onCapture(file);
        stopCamera();
        onClose();
      })
      .catch((error) => {
        console.error("❌ Failed to convert captured image to file:", error);
      });
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Camera className="h-5 w-5 text-blue-600" />
            {title}
          </h3>
          <button
            onClick={handleClose}
            aria-label={t.medxCamClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Camera View */}
        <div className="relative bg-black">
          {error ? (
            <div className="flex items-center justify-center h-96 text-white p-8 text-center">
              <div>
                <Camera className="h-16 w-16 mx-auto mb-4 text-red-400" />
                <p className="text-lg mb-2">{t.medxCamUnavailable}</p>
                <p className="text-sm text-gray-400">{t.medxCamAccessDenied}</p>
              </div>
            </div>
          ) : capturedImage ? (
            <img
              src={capturedImage}
              alt={t.medxCamCapturedAlt}
              className="w-full h-auto max-h-[60vh] object-contain"
            />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-auto max-h-[60vh] object-contain"
            />
          )}

          <canvas ref={canvasRef} className="hidden" />
        </div>

        {/* Controls */}
        {!error && (
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            {capturedImage ? (
              <div className="flex gap-3 justify-center">
                <button
                  onClick={retakePhoto}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <RotateCw className="h-5 w-5" />
                  {t.medxCamRetake}
                </button>
                <button
                  onClick={confirmPhoto}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Check className="h-5 w-5" />
                  {t.medxCamUsePhoto}
                </button>
              </div>
            ) : (
              <div className="flex gap-3 justify-center">
                <button
                  onClick={switchCamera}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <RotateCw className="h-5 w-5" />
                  {t.medxCamSwitch}
                </button>
                <button
                  onClick={capturePhoto}
                  className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
                >
                  <Camera className="h-6 w-6" />
                  {t.medxCamTake}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
